/**
 * Capitalizes the first character of a string, leaving the rest unchanged.
 *
 * @param str - The input string to be transformed.
 * @returns The string with the first character capitalized and the rest unchanged.
 *
 * @example
 * ```typescript
 * capsFirst("hello");  // "Hello"
 * capsFirst("WORLD");  // "WORLD"
 * capsFirst("tEsTiNg"); // "TEsTiNg"
 * ```
 */
export const capsFirst = function (str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Converts a given string to sentence case.
 *
 * @param str - The input string to be transformed.
 * @returns The string with the first character capitalized and the rest in lowercase.
 *
 * @example
 * ```typescript
 * toSentenceCase("hello WORLD"); // "Hello world"
 * toSentenceCase("tEsTiNg");     // "Testing"
 * ```
 */
export const toSentenceCase = function (str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

const NBSP_ENTITY_REGEX = /&nbsp;/g
/**
 * A single non-breaking space used as a word separator. Quill 2 escapes every space of a text leaf
 * in `getSemanticHTML()`, which makes long texts impossible to wrap.
 *
 * @see https://github.com/quilljs/quill/issues/4509
 */
const WORD_SEPARATOR_NBSP_REGEX = /([^\s>])\u00A0(?![\s\u00A0])/g

/**
 * Replaces word separating non-breaking spaces in an html string so that long texts can wrap
 * normally. Deliberate ones are kept: runs of 2+ (indentation) and those right after a tag or a
 * regular space.
 *
 * @param html - The html string to normalize.
 * @returns The html string with its word separating non-breaking spaces turned into regular spaces.
 *
 * @example
 * ```typescript
 * normalizeHtmlNbsp("<p>some&nbsp;content</p>"); // "<p>some content</p>"
 * normalizeHtmlNbsp("<p>&nbsp;&nbsp;indented</p>"); // "<p>&nbsp;&nbsp;indented</p>" (untouched)
 * ```
 */
export const normalizeHtmlNbsp = (html: string) =>
  html.replace(NBSP_ENTITY_REGEX, '\u00A0').replace(WORD_SEPARATOR_NBSP_REGEX, '$1 ')

/**
 * Matches a block level line that carries no content at all, e.g. `<p></p>` or
 * `<h2 class="ql-align-center"></h2>`. Lines holding a non-breaking space are left alone, they
 * already render as a line.
 */
const EMPTY_HTML_LINE_REGEX = /<(p|h[1-6])((?:\s[^>]*)?)>[ \t\r\n]*<\/\1\s*>/gi

/**
 * Puts a `<br>` back into empty block level lines. Quill 2 serializes a blank line as `<p></p>`
 * through `getSemanticHTML()`, which collapses to nothing once rendered, so the blank lines an
 * author adds between paragraphs disappear on the storefront.
 *
 * @param html - The html string to restore.
 * @returns The html string with its empty lines turned into `<p><br></p>`.
 *
 * @example
 * ```typescript
 * restoreEmptyHtmlLines("<p>a</p><p></p><p>b</p>"); // "<p>a</p><p><br></p><p>b</p>"
 * restoreEmptyHtmlLines("<p><br></p>"); // "<p><br></p>" (untouched)
 * ```
 */
export const restoreEmptyHtmlLines = (html: string) =>
  html.replace(EMPTY_HTML_LINE_REGEX, '<$1$2><br></$1>')
